import { Language } from '@capturi/superpowers/src/shared/types'
import { Box, Button, Flex, HStack, Tooltip, useToast } from '@chakra-ui/react'
import { Trans, t } from '@lingui/macro'
import { FC, useMemo, useState } from 'react'
import isEqual from 'react-fast-compare'
import { Webhook } from '../../ApiAndWebhooks/Api/Webhooks/models/Webhooks'
import { Webhooks } from '../../ApiAndWebhooks/Webhooks/Index'
import { CopilotConfig } from '../api/types'
import {
  useCreateCopilotConfig,
  useUpdateCopilotConfig,
} from '../api/useOrganizationSettings'
import { BaseConfig } from './BaseConfig'
import { LiveSummary } from './LiveSummary'
import { duplicateQueues } from './duplicateQueues'

type Props = {
  copilotConfig: CopilotConfig
  orgUid: string
}

const selectLiveSummaryWebhooks = (data: Webhook[]): Webhook[] => {
  return data?.filter((wh) => wh.event === 'LiveSummaryCreated')
}

export const AgentCoPilotConfig: FC<Props> = ({ copilotConfig, orgUid }) => {
  const toast = useToast()
  const [state, setState] = useState<CopilotConfig>({
    language: copilotConfig?.language || 'da-DK',
    provider: copilotConfig?.provider || 'AzureOpenAI',
    anonymize: copilotConfig?.anonymize || true,
    queuePrompts: copilotConfig?.queuePrompts || [],
    tokens: copilotConfig?.tokens || [],
    organizationUid: orgUid ?? null,
    modules: copilotConfig?.modules || {
      handover: false,
      previousInteractions: false,
      liveSummary: false,
      askCopilot: false,
    },
  })
  const { duplicates } = duplicateQueues(state.queuePrompts)

  const { mutate: updateCopilotConfig, isPending } = useUpdateCopilotConfig(
    state.organizationUid,
  )
  const { mutate: createCopilotConfig } = useCreateCopilotConfig(orgUid)

  const handleCreateCopilotConfig = (config: CopilotConfig) => {
    createCopilotConfig(config, {
      onSuccess: () => {
        toast({
          title: 'Copilot config created',
          status: 'success',
        })
      },
      onError: (error) => {
        toast({
          title: 'Failed to create copilot config',
          status: 'error',
          description: error.message,
        })
      },
    })
  }

  const handleUpdateCopilotConfig = (config: Partial<CopilotConfig>) => {
    updateCopilotConfig(config, {
      onSuccess: () => {
        toast({
          title: 'Copilot config updated',
          status: 'success',
        })
      },
      onError: (error) => {
        toast({
          title: 'Failed to update copilot config',
          status: 'error',
          description: error.message,
        })
      },
    })
  }

  const cancelConfigChanges = () => {
    setState(copilotConfig)
  }
  const isConfigChanged = useMemo(() => {
    return !isEqual(copilotConfig, state)
  }, [copilotConfig, state])

  return (
    <Box mt={8}>
      <Flex flexDirection="column" gap={4} align="flex-end">
        <Flex gap={2}>
          <Button
            isDisabled={!isConfigChanged || isPending}
            onClick={cancelConfigChanges}
          >
            <Trans>Cancel changes</Trans>
          </Button>
          <Tooltip
            isDisabled={!duplicates.size}
            hasArrow
            placement="top"
            label={t`Remove queue duplicate(s) to save changes`}
          >
            <Button
              isDisabled={
                !isConfigChanged ||
                duplicates.size > 0 ||
                !state.queuePrompts.length
              }
              isLoading={isPending}
              colorScheme="primary"
              onClick={() =>
                !copilotConfig
                  ? handleCreateCopilotConfig(state)
                  : handleUpdateCopilotConfig(state)
              }
            >
              <Trans>Save Agent Co-pilot</Trans>
            </Button>
          </Tooltip>
        </Flex>
        <HStack flex="1" align="stretch">
          <LiveSummary
            copilotConfig={copilotConfig}
            state={state}
            setState={setState}
          />
          <Box w="1px" bg="greys.100" mx={4} />
          <BaseConfig
            copilotConfig={state}
            onLanguageChange={(language) =>
              setState((s) => ({ ...s, language: language as Language }))
            }
            onProviderChange={(provider) =>
              setState((s) => ({
                ...s,
                provider: provider as 'OpenAI' | 'AzureOpenAI',
              }))
            }
            onModuleChange={(key, value) =>
              setState((s) => ({
                ...s,
                modules: { ...s.modules, [key]: value },
              }))
            }
          />
        </HStack>
      </Flex>
      <Box mt={8}>
        <Webhooks select={selectLiveSummaryWebhooks} isCopilotWebhooks={true} />
      </Box>
    </Box>
  )
}
